/**
 * Alerts — Stage 9 surface.
 *
 * Two layers:
 *  1. Personal rules (Stage 3) — local-engine, browser-side. Subscribes
 *     to the live order stream on this device only. Rules in localStorage.
 *  2. Team rules + history — server-backed via `/api/bi/alerts/*`. Rules
 *     are shared across operators; events are an inbox everyone can ack
 *     or snooze together.
 */
import { useState } from 'react';
import {
  ShieldCheck,
  BellOff,
  BellRing,
  Trash2,
  Plus,
  Users,
  Wifi,
  Loader2,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  useAlertRules as useLocalAlertRules,
  requestAlertPermission,
  type AlertRuleKind as LocalAlertRuleKind,
} from '@/features/alerts/engine';
import {
  useAlertRules,
  useAlertHistory,
  createAlertRule,
  patchAlertRule,
  deleteAlertRule,
  actOnAlertEvent,
  type AlertKind,
  type AlertRule,
  type AlertSeverity,
  type AlertEvent,
} from '@/api/bi';
import { storage } from '@/lib/storage';
import { useSettingsStore } from '@/api/bi/settings';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

const LOCAL_KIND_LABEL: Record<LocalAlertRuleKind, string> = {
  order_age: 'Order age (sec)',
  cancellation_spike: 'Cancellations / minute',
  store_offline: 'Store offline (sec)',
};

const SEVERITY_TONE: Record<AlertSeverity, 'info' | 'warning' | 'danger'> = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
};

type TabId = 'personal' | 'team';

export function AlertsPage() {
  const [tab, setTab] = useState<TabId>('personal');

  return (
    <>
      <PageHeader
        eyebrow="Trust"
        title="Alerts"
        description="Personal rules fire from this device. Team rules sync across every operator."
      />

      <div className="mt-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
          <TabsList className="mb-4">
            <TabsTrigger value="personal">
              <Wifi className="mr-1.5 h-3.5 w-3.5" /> Personal
            </TabsTrigger>
            <TabsTrigger value="team">
              <Users className="mr-1.5 h-3.5 w-3.5" /> Team inbox
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === 'personal' ? <PersonalPanel /> : <TeamPanel />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Personal (local engine)
// ---------------------------------------------------------------------------

function PersonalPanel() {
  const { rules, history, update, add, remove, ack, clearHistory } = useLocalAlertRules();
  const [enabled, setEnabled] = useState<boolean>(() => storage.getPrefs().alerts_enabled !== false);
  const [draftKind, setDraftKind] = useState<LocalAlertRuleKind>('order_age');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftThreshold, setDraftThreshold] = useState<number>(1800);

  function toggleEngine(next: boolean) {
    setEnabled(next);
    storage.setPrefs({ alerts_enabled: next });
    try {
      useSettingsStore.getState().patchLocal({ prefs: { alerts_enabled: next } });
    } catch {
      /* noop — settings store may not be ready (logged-out boot, tests). */
    }
    notify[next ? 'success' : 'message'](next ? 'Alert engine enabled' : 'Alert engine paused', {
      description: next
        ? 'Reload the page to start the live subscription.'
        : 'New events will not trigger toasts or chimes until you re-enable.',
    });
  }

  async function askPermission() {
    const result = await requestAlertPermission();
    notify.message(`Notifications: ${result}`);
  }

  function addRule() {
    if (!draftLabel.trim()) {
      notify.error('Give your rule a label first.');
      return;
    }
    add({
      id: `${draftKind}-${Date.now()}`,
      kind: draftKind,
      label: draftLabel.trim(),
      threshold: draftThreshold,
      enabled: true,
      cooldown_sec: 60,
    });
    setDraftLabel('');
    notify.success('Rule added.');
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge tone={enabled ? 'success' : 'neutral'} className="gap-1">
          {enabled ? <BellRing className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
          engine {enabled ? 'on' : 'off'}
        </Badge>
        <Button size="sm" variant="outline" onClick={() => toggleEngine(!enabled)}>
          {enabled ? 'Pause' : 'Enable'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => void askPermission()}>
          Browser permission
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <Card>
          <CardHeader>
            <CardTitle>Rules (this device)</CardTitle>
            <Badge tone="neutral">{rules.length}</Badge>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            {rules.length === 0 ? (
              <EmptyState
                icon={<ShieldCheck className="h-5 w-5" />}
                title="No rules yet"
                description="Add a rule below — it'll fire as soon as the live stream crosses the threshold."
              />
            ) : (
              <ul className="space-y-1.5">
                {rules.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2">
                    <button
                      type="button"
                      onClick={() => update(r.id, { enabled: !r.enabled })}
                      className={cn(
                        'press inline-flex h-5 w-5 items-center justify-center rounded-full border',
                        r.enabled ? 'border-success bg-success/10 text-success' : 'border-muted text-muted-foreground',
                      )}
                      aria-label={r.enabled ? 'Disable rule' : 'Enable rule'}
                    >
                      {r.enabled ? <BellRing className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{r.label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {LOCAL_KIND_LABEL[r.kind]} · threshold {r.threshold}
                        {r.cooldown_sec ? ` · cooldown ${r.cooldown_sec}s` : ''}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} aria-label="Delete rule">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-2 rounded-md border border-dashed border-border bg-surface px-3 py-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Add rule</p>
              <div className="grid grid-cols-2 gap-2">
                <Select value={draftKind} onValueChange={(v) => setDraftKind(v as LocalAlertRuleKind)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order_age">Order stuck too long</SelectItem>
                    <SelectItem value="cancellation_spike">Cancellation spike</SelectItem>
                    <SelectItem value="store_offline">Store offline (soon)</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={draftThreshold}
                  onChange={(e) => setDraftThreshold(Number(e.target.value) || 0)}
                  placeholder={LOCAL_KIND_LABEL[draftKind]}
                  className="h-8"
                />
                <Input
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  placeholder="Label (e.g. Order stuck > 30 min)"
                  className="col-span-2 h-8"
                />
                <Button size="sm" onClick={addRule} className="col-span-2 gap-1">
                  <Plus className="h-3.5 w-3.5" /> Add rule
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>History (this device)</CardTitle>
            <div className="flex items-center gap-2">
              <Badge tone="neutral">{history.length}</Badge>
              <Button size="sm" variant="ghost" onClick={() => clearHistory()} aria-label="Clear history">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {history.length === 0 ? (
              <EmptyState
                icon={<BellOff className="h-5 w-5" />}
                title="Nothing has fired yet"
                description="When a rule trips you'll see the hit here, in a toast and (optionally) via a desktop notification."
              />
            ) : (
              <ul className="divide-y divide-border">
                {history.slice().reverse().map((h) => (
                  <li
                    key={h.id}
                    className={cn(
                      'flex items-start justify-between gap-2 py-2.5 text-sm',
                      h.acknowledged && 'opacity-70',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{h.label}</p>
                      <p className="text-[11px] text-muted-foreground">{h.message}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-[11px] text-muted-foreground">{fmt.ago(new Date(h.ts))}</span>
                      {!h.acknowledged ? (
                        <Button size="sm" variant="outline" onClick={() => ack(h.id)}>Ack</Button>
                      ) : (
                        <Badge tone="success">acked</Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Team (server-backed)
// ---------------------------------------------------------------------------

function TeamPanel() {
  const qc = useQueryClient();
  const rulesQ = useAlertRules();
  const [activeOnly, setActiveOnly] = useState(true);
  const historyQ = useAlertHistory({ days: 7, onlyActive: activeOnly });

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
      <TeamRulesCard rulesQ={rulesQ} qc={qc} />
      <TeamHistoryCard
        historyQ={historyQ}
        activeOnly={activeOnly}
        onToggleActive={() => setActiveOnly((v) => !v)}
        qc={qc}
      />
    </div>
  );
}

function TeamRulesCard({ rulesQ, qc }: { rulesQ: ReturnType<typeof useAlertRules>; qc: ReturnType<typeof useQueryClient> }) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftKind, setDraftKind] = useState<AlertKind>('order_in_status_too_long');
  const [draftSeverity, setDraftSeverity] = useState<AlertSeverity>('warning');
  const [draftThreshold, setDraftThreshold] = useState<number>(900);

  async function toggle(rule: AlertRule, next: boolean) {
    setBusyId(rule.id);
    try {
      await patchAlertRule(rule.id, { enabled: next });
      await qc.invalidateQueries({ queryKey: ['bi', 'alert-rules'] });
    } catch (e) {
      notify.error('Could not update rule.');
    } finally {
      setBusyId(null);
    }
  }

  async function destroy(rule: AlertRule) {
    setBusyId(rule.id);
    try {
      await deleteAlertRule(rule.id);
      await qc.invalidateQueries({ queryKey: ['bi', 'alert-rules'] });
      notify.success('Rule removed.');
    } catch (e) {
      notify.error('Could not delete rule.');
    } finally {
      setBusyId(null);
    }
  }

  async function addRule() {
    if (!draftName.trim()) {
      notify.error('Give your rule a name.');
      return;
    }
    try {
      await createAlertRule({
        name: draftName.trim(),
        kind: draftKind,
        severity: draftSeverity,
        threshold_seconds: draftThreshold,
        enabled: true,
      });
      setDraftName('');
      await qc.invalidateQueries({ queryKey: ['bi', 'alert-rules'] });
      notify.success('Rule synced.');
    } catch (e) {
      notify.error('Could not add rule.');
    }
  }

  const data = rulesQ.data;
  const rules = data?.rules ?? [];
  const kinds = data?.kinds ?? [];
  const severities = data?.severities ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team rules</CardTitle>
        <Badge tone="neutral">{rules.length}</Badge>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        {rulesQ.isError ? (
          <ErrorState description="Couldn't load rules." onRetry={() => void rulesQ.refetch()} />
        ) : rulesQ.isLoading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-md bg-muted/40" />
            ))}
          </div>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-5 w-5" />}
            title="No team rules"
            description="Add a rule and every operator will see it the next time they refresh."
          />
        ) : (
          <ul className="space-y-1.5">
            {rules.map((r) => (
              <li key={r.id} className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2">
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => void toggle(r, !r.enabled)}
                  className={cn(
                    'press inline-flex h-5 w-5 items-center justify-center rounded-full border',
                    r.enabled ? 'border-success bg-success/10 text-success' : 'border-muted text-muted-foreground',
                  )}
                  aria-label={r.enabled ? 'Disable rule' : 'Enable rule'}
                >
                  {busyId === r.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : r.enabled ? (
                    <BellRing className="h-3 w-3" />
                  ) : (
                    <BellOff className="h-3 w-3" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {kinds.find((k) => k.id === r.kind)?.label ?? r.kind} · {r.threshold_seconds}s
                  </p>
                </div>
                <Badge tone={SEVERITY_TONE[r.severity]}>{r.severity}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === r.id}
                  onClick={() => void destroy(r)}
                  aria-label="Delete rule"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-2 rounded-md border border-dashed border-border bg-surface px-3 py-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Add team rule</p>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="e.g. Order stuck > 15 min"
              className="col-span-2 h-8"
            />
            <Select value={draftKind} onValueChange={(v) => setDraftKind(v as AlertKind)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(kinds.length
                  ? kinds
                  : [
                      { id: 'order_in_status_too_long' as AlertKind, label: 'Order stuck in status' },
                      { id: 'store_offline_too_long' as AlertKind, label: 'Store offline too long' },
                      { id: 'cancel_spike' as AlertKind, label: 'Cancel spike' },
                      { id: 'custom' as AlertKind, label: 'Custom' },
                    ]
                ).map((k) => (
                  <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={draftSeverity} onValueChange={(v) => setDraftSeverity(v as AlertSeverity)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(severities.length
                  ? severities
                  : [
                      { id: 'info' as AlertSeverity, label: 'Info' },
                      { id: 'warning' as AlertSeverity, label: 'Warning' },
                      { id: 'critical' as AlertSeverity, label: 'Critical' },
                    ]
                ).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={draftThreshold}
              onChange={(e) => setDraftThreshold(Number(e.target.value) || 0)}
              placeholder="Threshold seconds"
              className="col-span-2 h-8"
            />
            <Button size="sm" onClick={() => void addRule()} className="col-span-2 gap-1">
              <Plus className="h-3.5 w-3.5" /> Sync to team
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function TeamHistoryCard({
  historyQ,
  activeOnly,
  onToggleActive,
  qc,
}: {
  historyQ: ReturnType<typeof useAlertHistory>;
  activeOnly: boolean;
  onToggleActive: () => void;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const data = historyQ.data;
  const events = data?.events ?? [];
  const totals = data?.totals;

  async function ack(ev: AlertEvent) {
    setBusyId(ev.id);
    try {
      await actOnAlertEvent(ev.id, 'ack');
      await qc.invalidateQueries({ queryKey: ['bi', 'alert-history'] });
    } catch (e) {
      notify.error('Could not acknowledge event.');
    } finally {
      setBusyId(null);
    }
  }

  async function snooze(ev: AlertEvent) {
    setBusyId(ev.id);
    try {
      await actOnAlertEvent(ev.id, 'snooze', 30);
      await qc.invalidateQueries({ queryKey: ['bi', 'alert-history'] });
      notify.success('Snoozed for 30 min.');
    } catch (e) {
      notify.error('Could not snooze event.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team inbox</CardTitle>
        <div className="flex items-center gap-2">
          {totals ? (
            <Badge tone={totals.active > 0 ? 'warning' : 'success'}>
              {totals.active} active
            </Badge>
          ) : null}
          <Button size="sm" variant="outline" onClick={onToggleActive}>
            {activeOnly ? 'Show all' : 'Active only'}
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        {historyQ.isError ? (
          <ErrorState description="Couldn't load events." onRetry={() => void historyQ.refetch()} />
        ) : historyQ.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-md bg-muted/40" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-5 w-5" />}
            title="All quiet"
            description={activeOnly ? 'No active events. Your operators are caught up.' : 'No events recorded in the last 7 days.'}
          />
        ) : (
          <ul className="divide-y divide-border">
            {events.map((ev) => {
              const acked = !!ev.acknowledged_at;
              const snoozed = ev.snoozed_until && new Date(ev.snoozed_until) > new Date();
              return (
                <li key={ev.id} className={cn('flex items-start gap-3 py-2.5 text-sm', acked && 'opacity-70')}>
                  <Badge tone={SEVERITY_TONE[ev.severity]}>{ev.severity}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{ev.rule_name ?? 'Custom event'}</p>
                    <p className="text-[11px] text-muted-foreground">{ev.message}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">
                      {ev.fired_at ? fmt.ago(ev.fired_at) : ''}
                    </span>
                    {acked ? (
                      <Badge tone="success">acked</Badge>
                    ) : snoozed ? (
                      <Badge tone="info">snoozed</Badge>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === ev.id}
                          onClick={() => void snooze(ev)}
                        >
                          Snooze 30m
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === ev.id}
                          onClick={() => void ack(ev)}
                        >
                          Ack
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
